import { useState } from 'react'
import { Box } from 'lucide-react'
import RoomScanCard from './RoomScanCard.jsx'
import RoomScanPreviewModal from './RoomScanPreviewModal.jsx'
import RoomScanRenameModal from './RoomScanRenameModal.jsx'
import {
  useDeleteRoomScan,
  usePatchRoomScan,
  useRoomScans,
} from './useRoomScans.js'

export default function RoomScansTab() {
  const { data: scans = [], isLoading, isError, error, refetch } = useRoomScans()
  const patch = usePatchRoomScan()
  const remove = useDeleteRoomScan()
  const [previewing, setPreviewing] = useState(null)
  const [renaming, setRenaming] = useState(null)

  if (isLoading) {
    return <div className="card card--lg muted">Loading room scans…</div>
  }

  if (isError) {
    return (
      <div className="card card--lg" role="alert">
        <div className="col" style={{ gap: 8 }}>
          <strong>Could not load scans</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            {error?.uiMessage || error?.message || 'Unknown error'}
          </span>
          <button className="btn btn--sm" onClick={() => refetch()}>Retry</button>
        </div>
      </div>
    )
  }

  if (scans.length === 0) {
    return (
      <section className="card card--lg empty-state" style={{ minHeight: 220 }}>
        <div className="col" style={{ alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: 'var(--accent-soft)',
              color: 'var(--accent-strong)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box size={18} />
          </span>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>No room scans yet</div>
          <div className="muted" style={{ maxWidth: 460, fontSize: 13, textAlign: 'center' }}>
            Capture a floor with the ARrow Scanner iOS app. Uploaded scans will appear here as USDZ assets you can preview, download, and link to floors.
          </div>
        </div>
      </section>
    )
  }

  const handleDelete = (scan) => {
    const ok = window.confirm(`Delete "${scan.name}"? This removes the USDZ from storage.`)
    if (!ok) return
    remove.mutate({ id: scan.id })
  }

  const handleRenameSubmit = (patchBody) => {
    if (!renaming) return
    patch.mutate(
      { id: renaming.id, patch: patchBody },
      { onSuccess: () => setRenaming(null) },
    )
  }

  return (
    <>
      <div className="room-scan-grid">
        {scans.map((scan) => (
          <RoomScanCard
            key={scan.id}
            scan={scan}
            onPreview={setPreviewing}
            onRename={setRenaming}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {previewing && (
        <RoomScanPreviewModal scan={previewing} onClose={() => setPreviewing(null)} />
      )}
      {renaming && (
        <RoomScanRenameModal
          scan={renaming}
          busy={patch.isPending}
          onCancel={() => setRenaming(null)}
          onSubmit={handleRenameSubmit}
        />
      )}
    </>
  )
}
